import { useId } from 'react';
import { RANK_PIPS, rankOf, suitOf, type Card } from '@cribbage/engine';
import { suitColour } from '~/table/CardSvg.js';
import { SuitGlyph, suitName } from '~/ui/SuitLine.js';

/** Shared card artwork, promoted from the card-face comparison. */
export type FaceDirection = 'daily' | 'marquee' | 'paper-cut' | 'clubhouse' | 'mono' | 'arcade';
type Point = readonly [number, number];

// Real pip counts, including the ten's two staggered center pips.
const PIPS: Readonly<Record<number, readonly Point[]>> = {
  2: [
    [125, 96],
    [125, 254],
  ],
  3: [
    [125, 96],
    [125, 175],
    [125, 254],
  ],
  4: [
    [83, 96],
    [167, 96],
    [83, 254],
    [167, 254],
  ],
  5: [
    [83, 96],
    [167, 96],
    [125, 175],
    [83, 254],
    [167, 254],
  ],
  6: [
    [83, 96],
    [167, 96],
    [83, 175],
    [167, 175],
    [83, 254],
    [167, 254],
  ],
  7: [
    [83, 96],
    [167, 96],
    [125, 136],
    [83, 175],
    [167, 175],
    [83, 254],
    [167, 254],
  ],
  8: [
    [83, 96],
    [167, 96],
    [125, 136],
    [83, 175],
    [167, 175],
    [125, 214],
    [83, 254],
    [167, 254],
  ],
  9: [
    [83, 96],
    [167, 96],
    [83, 149],
    [167, 149],
    [125, 175],
    [83, 201],
    [167, 201],
    [83, 254],
    [167, 254],
  ],
  10: [
    [83, 96],
    [167, 96],
    [125, 122],
    [83, 149],
    [167, 149],
    [83, 201],
    [167, 201],
    [125, 228],
    [83, 254],
    [167, 254],
  ],
};

function Pip({
  suit,
  x,
  y,
  size,
  flip = false,
}: {
  readonly suit: number;
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly flip?: boolean;
}): React.JSX.Element {
  return (
    <g transform={`translate(${x} ${y}) rotate(${flip ? 180 : 0})`}>
      <svg x={-size / 2} y={-size / 2} width={size} height={size} viewBox="0 0 24 24">
        <SuitGlyph suit={suitName(suit)} className="size-full" />
      </svg>
    </g>
  );
}

function Index({
  rank,
  suit,
  direction,
}: {
  readonly rank: number;
  readonly suit: number;
  readonly direction: FaceDirection;
}): React.JSX.Element {
  const wide = direction === 'marquee';
  return (
    <g>
      <text
        x="29"
        y="55"
        textAnchor="middle"
        fontSize={direction === 'mono' ? 50 : wide ? 58 : 55}
        fontWeight={direction === 'mono' ? 500 : 800}
        textLength={rank === 10 ? 40 : undefined}
        lengthAdjust="spacingAndGlyphs"
        fill="currentColor"
      >
        {RANK_PIPS[rank - 1]}
      </text>
      <Pip suit={suit} x={29} y={76} size={29} />
    </g>
  );
}

function NumberPips({
  rank,
  suit,
  cut = false,
  small = false,
}: {
  readonly rank: number;
  readonly suit: number;
  readonly cut?: boolean;
  readonly small?: boolean;
}): React.JSX.Element {
  return (
    <g>
      {PIPS[rank]?.map(([x, y], index) => (
        <g key={index} data-face-pip="true">
          {cut && (
            <g className="text-gray-300">
              <Pip suit={suit} x={x + 5} y={y + 5} size={46} flip={y > 175} />
            </g>
          )}
          <Pip suit={suit} x={x} y={y} size={small ? 35 : 46} flip={y > 175} />
        </g>
      ))}
    </g>
  );
}

/** An upright half is authored once; the other half really is reversible. */
function DailyCourt({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  const half = (
    <g strokeWidth="3.5" strokeLinejoin="round">
      <path d="M65 173V141Q125 112 185 141V173Z" fill="currentColor" />
      <path d="M84 146L108 172H78Z M166 146L142 172H172Z" fill="var(--court-gold)" />
      <rect
        x="102"
        y="91"
        width="46"
        height="53"
        rx="19"
        className="fill-ground-light stroke-ink"
      />
      {rank === 11 ? (
        <path d="M96 99V83H150L161 97Z" fill="var(--court-gold)" className="stroke-ink" />
      ) : (
        <path
          d={
            rank === 12
              ? 'M98 97L94 76L110 84L125 67L140 84L156 76L152 97Z'
              : 'M98 97L94 69L113 81L125 61L137 81L156 69L152 97Z'
          }
          fill="var(--court-gold)"
          className="stroke-ink"
        />
      )}
      <path d="M115 110v3m20-3v3" className="stroke-ink" strokeLinecap="round" />
      <path
        d={rank === 13 ? 'M109 124L125 143L141 124Z' : 'M119 127q6 5 12 0'}
        className={rank === 13 ? 'fill-ink' : 'fill-none stroke-ink'}
        strokeLinecap="round"
      />
      <g className="text-ground-light">
        <Pip suit={suit} x={125} y={160} size={22} />
      </g>
    </g>
  );
  return (
    <g>
      <rect x="62" y="61" width="126" height="228" rx="17" className="fill-ground-dark" />
      {half}
      <g transform="rotate(180 125 175)">{half}</g>
      <path d="M69 175H181" className="stroke-ground-light" strokeWidth="4" />
    </g>
  );
}

function CutCourt({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  const half = (
    <g>
      <path d="M65 173L79 134L153 117L186 173Z" fill="currentColor" />
      <path d="M80 138L130 125L115 174H65Z" fill="var(--court-gold)" />
      <path
        d="M105 92L151 88L157 120L137 142L112 126Z"
        className="fill-ground-light stroke-ink"
        strokeWidth="3"
      />
      <path
        d={
          rank === 11
            ? 'M98 98L101 75L147 68L158 86Z'
            : rank === 12
              ? 'M99 97L88 74L113 80L123 59L139 77L157 63L157 90Z'
              : 'M101 96L91 62L115 73L128 48L141 72L164 61L157 90Z'
        }
        fill="var(--court-gold)"
        className="stroke-ink"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path d="M140 102v7l9 4h-10" className="fill-none stroke-ink" strokeWidth="3" />
      <circle cx="126" cy="106" r="2.5" className="fill-ink" />
      {rank === 13 && <path d="M122 122L143 122L136 139Z" className="fill-ink" />}
      <g className="text-ground-light">
        <Pip suit={suit} x={159} y={151} size={23} />
      </g>
    </g>
  );
  return (
    <g>
      {half}
      <g transform="rotate(180 125 175)">{half}</g>
      <path d="M63 173L187 177" className="stroke-ground-light" strokeWidth="5" />
    </g>
  );
}

function ClubCourt({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  return (
    <g strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <rect
        x="62"
        y="61"
        width="126"
        height="228"
        rx="63"
        className="fill-ground-light"
        stroke="currentColor"
      />
      <path d="M67 251Q69 191 125 191Q181 191 183 251" fill="currentColor" />
      <path d="M104 190L125 221L146 190" fill="var(--court-gold)" />
      {rank === 12 && (
        <path d="M83 160V119Q125 74 167 119V179L146 194H102L82 177Z" fill="currentColor" />
      )}
      <rect
        x="94"
        y="109"
        width="62"
        height="82"
        rx="29"
        className="fill-ground-light stroke-ink"
      />
      <path
        d={
          rank === 11
            ? 'M88 125L98 94L155 96L166 120Z'
            : rank === 12
              ? 'M92 121L87 91L108 101L125 77L141 101L163 91L158 121Z'
              : 'M92 122L86 82L111 99L125 69L140 99L164 82L158 122Z'
        }
        fill="var(--court-gold)"
        className="stroke-ink"
      />
      <path d="M111 143v4m28-4v4M119 163q7 7 14 0" className="fill-none stroke-ink" />
      {rank === 13 && (
        <path
          d="M96 162L111 174L125 169L141 174L155 162L146 190L125 204L104 190Z"
          className="fill-ink"
        />
      )}
      <g className="text-ground-light">
        <Pip suit={suit} x={125} y={245} size={37} />
      </g>
      <path d="M101 278h48" fill="none" stroke="currentColor" />
    </g>
  );
}

function Marquee({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  return (
    <g>
      <path d="M64 74H186M64 276H186" stroke="currentColor" strokeWidth="5" />
      {rank >= 11 && (
        <path d="M99 103L96 85L111 93L125 78L139 93L154 85L151 103Z" fill="var(--court-gold)" />
      )}
      <text
        x="125"
        y={rank === 12 ? 207 : 217}
        textAnchor="middle"
        fill="currentColor"
        fontWeight="800"
        fontSize={rank === 10 ? 131 : rank === 12 ? 139 : 161}
        letterSpacing="-6"
        textLength={rank === 10 ? 139 : undefined}
        lengthAdjust="spacingAndGlyphs"
      >
        {RANK_PIPS[rank - 1]}
      </text>
      <Pip suit={suit} x={125} y={248} size={40} />
    </g>
  );
}

function Mono({ rank, suit }: { readonly rank: number; readonly suit: number }): React.JSX.Element {
  return (
    <g>
      <rect
        x="65"
        y="72"
        width="120"
        height="206"
        rx="60"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <text
        x="125"
        y="173"
        textAnchor="middle"
        fill="currentColor"
        fontWeight="500"
        fontSize={rank === 10 ? 77 : 98}
        textLength={rank === 10 ? 91 : undefined}
        lengthAdjust="spacingAndGlyphs"
      >
        {RANK_PIPS[rank - 1]}
      </text>
      <path d="M104 186h42" stroke="currentColor" strokeWidth="2" />
      <Pip suit={suit} x={125} y={224} size={62} />
      {rank >= 11 &&
        Array.from({ length: rank - 10 }, (_, index) => (
          <circle
            key={index}
            cx={125 + (index - (rank - 11) / 2) * 12}
            cy="96"
            r="3"
            fill="currentColor"
          />
        ))}
    </g>
  );
}

/** G uses the brand's chunky geometry inside the unchanged card stock.
 * The courts are two-ended cutouts: broad silhouettes, one gold accent,
 * and no fine engraving that disappears on the Cross grid. */
function ArcadeCourt({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  const half = (
    <g strokeWidth="5" strokeLinejoin="round" strokeLinecap="round">
      <path
        d="M68 173V150Q68 138 81 133L107 124H143L169 133Q182 138 182 150V173Z"
        fill="currentColor"
      />
      <path d="M85 135L109 127L151 173H123Z" className="fill-ground-light" />
      {rank === 12 && <path d="M91 98Q91 72 125 72Q159 72 159 98V141H91Z" fill="currentColor" />}
      <path
        d="M103 88H147V111Q147 133 125 133Q103 133 103 111Z"
        className="fill-ground-light stroke-ink"
      />
      <path
        d={
          rank === 11
            ? 'M95 91V77L139 68L158 91Z'
            : rank === 12
              ? 'M100 89L95 65L112 74L125 56L138 74L155 65L150 89Z'
              : 'M100 89L94 55L113 70L125 48L137 70L156 55L150 89Z'
        }
        fill="var(--court-gold)"
        className="stroke-ink"
      />
      <path d="M114 102V106M136 102V106" className="stroke-ink" />
      {rank === 13 ? (
        <path
          d="M104 116L115 121L125 117L135 121L146 116L141 132L125 143L109 132Z"
          className="fill-ink"
        />
      ) : (
        <path d="M119 119Q125 124 131 119" className="fill-none stroke-ink" />
      )}
      <g className="text-ground-light">
        <Pip suit={suit} x={162} y={155} size={25} />
      </g>
    </g>
  );
  return (
    <g>
      <rect x="65" y="48" width="126" height="260" rx="24" className="fill-gray-300" />
      <rect
        x="59"
        y="42"
        width="126"
        height="260"
        rx="24"
        className="fill-ground-dark stroke-ink"
        strokeWidth="5"
      />
      {half}
      <g transform="rotate(180 125 175)">{half}</g>
      <path d="M68 175H182" className="stroke-ground-light" strokeWidth="6" />
    </g>
  );
}

function ArcadeArt({
  rank,
  suit,
}: {
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  if (rank >= 11) return <ArcadeCourt rank={rank} suit={suit} />;
  if (rank === 1)
    return (
      <g>
        <g className="text-gray-300">
          <Pip suit={suit} x={132} y={182} size={174} />
        </g>
        <Pip suit={suit} x={125} y={175} size={174} />
      </g>
    );
  return (
    <g>
      {PIPS[rank]?.map(([x, y], index) => (
        <g key={index} data-face-pip="true">
          <Pip suit={suit} x={x} y={y} size={rank >= 9 ? 49 : 54} flip={y > 175} />
        </g>
      ))}
    </g>
  );
}

function FaceArt({
  direction,
  rank,
  suit,
}: {
  readonly direction: FaceDirection;
  readonly rank: number;
  readonly suit: number;
}): React.JSX.Element {
  if (direction === 'arcade') return <ArcadeArt rank={rank} suit={suit} />;
  if (direction === 'marquee') return <Marquee rank={rank} suit={suit} />;
  if (direction === 'mono') return <Mono rank={rank} suit={suit} />;
  if (rank >= 11) {
    if (direction === 'paper-cut') return <CutCourt rank={rank} suit={suit} />;
    if (direction === 'clubhouse') return <ClubCourt rank={rank} suit={suit} />;
    return <DailyCourt rank={rank} suit={suit} />;
  }
  if (rank === 1)
    return (
      <g>
        {direction === 'daily' && (
          <rect x="63" y="108" width="124" height="134" rx="35" className="fill-ground-dark" />
        )}
        {direction === 'clubhouse' && (
          <rect
            x="63"
            y="78"
            width="124"
            height="194"
            rx="62"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
          />
        )}
        {direction === 'paper-cut' && (
          <g className="text-gray-300">
            <Pip suit={suit} x={133} y={183} size={143} />
          </g>
        )}
        <Pip suit={suit} x={125} y={175} size={direction === 'clubhouse' ? 107 : 143} />
        {direction === 'clubhouse' && (
          <path d="M104 247h42" stroke="currentColor" strokeWidth="4" />
        )}
      </g>
    );
  return (
    <g>
      {direction === 'clubhouse' && (
        <rect
          x="59"
          y="65"
          width="132"
          height="220"
          rx="33"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      )}
      <NumberPips
        rank={rank}
        suit={suit}
        cut={direction === 'paper-cut'}
        small={direction === 'clubhouse'}
      />
    </g>
  );
}

export function CardFaceArt({
  card,
  direction,
  fourColor,
}: {
  readonly card: Card;
  readonly direction: FaceDirection;
  readonly fourColor: boolean;
}): React.JSX.Element {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const paper = `candidate-paper-${uid}`;
  const rank = rankOf(card);
  const suit = suitOf(card);
  return (
    <svg
      className="block size-full font-display"
      viewBox="0 0 250 350"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          className="card-paper-gradient card-paper-gradient-sketch"
          id={paper}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="0" />
          <stop offset="1" />
        </linearGradient>
      </defs>
      <rect
        x="0.5"
        y="0.5"
        width="249"
        height="349"
        rx="20"
        fill={`url(#${paper})`}
        stroke="rgba(20,17,14,0.25)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      <g color={suitColour(suit, fourColor)}>
        <Index rank={rank} suit={suit} direction={direction} />
        <g transform="rotate(180 125 175)">
          <Index rank={rank} suit={suit} direction={direction} />
        </g>
        <FaceArt direction={direction} rank={rank} suit={suit} />
      </g>
    </svg>
  );
}
